import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { loadStripe } from '@stripe/stripe-js';
import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';

import {
  Alert,
  AlertTitle,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Modal,
  Radio,
  RadioGroup,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import {
  COLOR_GREY_400,
  COLOR_GREY_600,
  COLOR_PRIMARY_1,
  COLOR_PRIMARY_2,
  COLOR_TEXT_GREY,
} from 'src/utils/colors';
import {
  COLOR_GREEN,
  COLOR_GREY,
  CountryCodeWithCurrency,
  CurrencyWithSubscriptionPrice,
  exchangeRates,
} from 'src/utils/const';
import {
  confirmCardPaymentRecursively,
  formatCurrencyBasedOnCountryCode,
  getPercentage,
  getYearlyUpgradeFinalDueAmount,
} from 'src/utils/functions';
import { callApi } from 'src/core/callApi';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Payment } from 'src/App/Dashboards/Market/Components';
import postOnSlack from 'src/core/postOnSlack';
import { logAmplitudeEvent } from 'src/utils/analytics';
import SenjaWidget from 'src/App/Dashboards/Market/Components/SenjaWidget';
import { useSetUserPaymentInterest } from 'src/hooks/useCustomHooks';
import { ProductIDOptions, stripeConfig } from 'src/utils/stripeConfig';

const UPGRADE_SUBSCRIPTION_URL_TEST =
  'https://9s2r54r64l.execute-api.us-east-2.amazonaws.com/default/upgradeStripeSubscription';
const UPGRADE_SUBSCRIPTION_URL =
  'https://9s2r54r64l.execute-api.us-east-2.amazonaws.com/default/upgradeStripeSubscription';
const IS_TEST_MODE: boolean = false;

const StripeContext = ({ plan, handleClose }) => {
  const theme = useTheme();
  const elements = useElements();
  const stripe = useStripe();
  const matches = useMediaQuery(theme.breakpoints.down(900));
  const userSignupCountryCode = useSelector((state: any) => state.dashboard.user.signupCountryCode);
  const userEmail = useSelector((state: any) => state.dashboard.user.email);
  const stripeAccount = useSelector((state: any) => state.dashboard.user.stripeAccount) || 'airbtics_uk';
  const search = useSelector((state: any) => state.dashboard.search);
  const userLocalCurrency = CountryCodeWithCurrency[userSignupCountryCode] || 'USD';
  const subscriptionPriceAccordingToUserLocalCurrency =
    CurrencyWithSubscriptionPrice[userLocalCurrency];
  const [subscriptionUpgradeLoading, setSubscriptionUpgradeLoading] = useState(false);
  const duration = 'yearly';
  const [paymentFailed, setPaymentFailed] = useState<boolean>(false);
  const [paymentFailedReason, setPaymentFailedReason] = useState<string>('');
  const [showNewCardForm, setShowNewCardForm] = useState<boolean>(false);

  const [finalDueAmount, setFinalDueAmount] = useState<number | null>(null);

  const { triggerPaymentInterest } = useSetUserPaymentInterest();

  useEffect(() => {
    triggerPaymentInterest();
  }, []);

  useEffect(() => {
    logAmplitudeEvent('yearly_upgrade_view_from_cancellation', {
      plan_name: plan,
      plan_duration: duration,
      is_upgrade: true,
      view: '',
      visited_from: '',
    });
  }, [plan, duration]);

  const currencySymbol = exchangeRates[userLocalCurrency].symbol;

  const monthlyPrice = parseFloat(subscriptionPriceAccordingToUserLocalCurrency[plan]['monthly']);
  const notDiscountedYearlyPrice = monthlyPrice * 12;
  const yearlyPrice = subscriptionPriceAccordingToUserLocalCurrency[plan]['yearly'];
  const monthlyDiscount = subscriptionPriceAccordingToUserLocalCurrency[plan]['monthly'];

  const totalYearlyDiscount = notDiscountedYearlyPrice - yearlyPrice + monthlyDiscount;
  const formattedTotalYearlyDiscount = formatCurrencyBasedOnCountryCode(
    totalYearlyDiscount,
    userLocalCurrency
  );

  const discountPercentage = Math.abs(
    getPercentage(notDiscountedYearlyPrice - totalYearlyDiscount, notDiscountedYearlyPrice)
  ).toFixed(0);

  useEffect(() => {
    getYearlyUpgradeFinalDueAmount(plan, userLocalCurrency, setFinalDueAmount, {
      isTestMode: IS_TEST_MODE,
      stripeAccount,
    });
  }, [duration, plan, userLocalCurrency, stripeAccount]);

  const upgradeSubscription = async (paymentMethod: any | null = null) => {
    try {
      if (!stripe) {
        return;
      }
      setSubscriptionUpgradeLoading(true);
      const url = IS_TEST_MODE ? UPGRADE_SUBSCRIPTION_URL_TEST : UPGRADE_SUBSCRIPTION_URL;
      const productID = IS_TEST_MODE
        ? stripeConfig[stripeAccount]?.testProductIDOptions?.[plan]?.productID?.[duration]
        : stripeConfig[stripeAccount]?.productIDOptions?.[plan]?.productID?.[duration];
      const config = {
        queryParams: {
          newProductID: productID,
          newProductName: plan,
          newDuration: duration,
          operation: 'modify_subscription',
          cancellation_discount: true,
          ...(IS_TEST_MODE ? { isTest: true } : {}),
          payment_method_id: paymentMethod?.id || null,
        },
      };
      const resp = await callApi(url, config, true);
      setSubscriptionUpgradeLoading(false);
      if (resp.subscription_modification_status == 'success') {
        logAmplitudeEvent('subscribed', {
          plan_name: plan,
          plan_duration: duration,
          is_upgrade: true,
        });
        handleClose();
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else if (resp.subscription_modification_status == 'requires_action') {
        // requires 3D auth
        const clientSecret = resp.client_secret;
        const isAdditionalActionSuccess = await confirmCardPaymentRecursively(stripe, clientSecret);
        if (!isAdditionalActionSuccess) {
          postOnSlack(
            'tech',
            `3D Authentication failed for ${userEmail} during upgrade attempt to ${plan}-${duration}`
          );
          setPaymentFailed(true);
          setPaymentFailedReason('3D Authentication failed');
        } else {
          console.log('3D auth success. Re-fetch payment intent from lambda');
          setPaymentFailed(false);
          setPaymentFailedReason('');
          return upgradeSubscription();
        }
      } else if (resp.subscription_modification_status == 'requires_new_payment_method' || resp.subscription_modification_status == 'failed') {
        setPaymentFailed(true);
        setPaymentFailedReason(
          resp.error_message || 'Payment failed. Please try again with a different card.'
        );
        setShowNewCardForm(true);
      } else {
        setPaymentFailed(true);
        setPaymentFailedReason('');
      }
    } catch (err) {
      setSubscriptionUpgradeLoading(false);
      setPaymentFailed(true);
      setPaymentFailedReason((err as any)?.message);
    }
  };

  const handleNewCardSubmit = async event => {
    event.preventDefault();
    if (!stripe || !elements) return;

    setSubscriptionUpgradeLoading(true);
    try {
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        setPaymentFailed(true);
        setPaymentFailedReason('Card element not found. Please try again.');
        setSubscriptionUpgradeLoading(false);
        return;
      }

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      } as any);

      if (error) {
        setPaymentFailed(true);
        setPaymentFailedReason(error.message || 'Payment failed. Please try again.');
        setSubscriptionUpgradeLoading(false);
        return;
      }

      // Call the upgrade subscription API with the new payment method
      upgradeSubscription(paymentMethod);
    } catch (err) {
      setSubscriptionUpgradeLoading(false);
      setPaymentFailed(true);
      setPaymentFailedReason('An unexpected error occurred. Please try again.');
    }
  };

  return (
    <Grid
      container
      direction="column"
      justifyContent="flex-start"
      alignItems="flex-start"
      sx={{
        background: 'white',
        width: matches ? '100vw' : '500px',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        borderRadius: 4,
        outline: 'none !important',
        padding: 3,
        height: 'auto',
        maxHeight: matches ? '100dvh' : '90vh',
        overflow: 'auto',
      }}
    >
      <Grid container>
        <Grid
          item
          xs={12}
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            variant="h5"
            sx={{ color: COLOR_PRIMARY_2, fontWeight: 500, textTransform: 'capitalize' }}
          >
            Upgrade to Yearly Plan
          </Typography>
          <CloseIcon
            sx={{ color: COLOR_GREY_400, cursor: 'pointer', fontWeight: 'bold' }}
            onClick={handleClose}
          />
        </Grid>

        <Grid container item direction="row" justifyContent="flex-start" xs={12}>
          <Typography style={{ fontWeight: 'bold', marginTop: '20px' }}>Upgrade Summary</Typography>
        </Grid>

        <Grid item xs={12} sx={{ margin: '10px 0', width: '100%' }}>
          <Divider />
        </Grid>

        <Grid
          item
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          xs={12}
          sx={{ marginBottom: '10px' }}
        >
          <Typography variant="body1">Plan</Typography>
          <Typography variant="body1">Yearly - {plan} Plan</Typography>
        </Grid>

        <Grid item xs={12} sx={{ margin: '10px 0', width: '100%' }}>
          <Divider />
        </Grid>

        <Grid
          item
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          xs={12}
          sx={{ marginBottom: '10px' }}
        >
          <Typography variant="body2" sx={{ color: COLOR_TEXT_GREY }}>
            <span
              style={{
                padding: '2px 4px',
                background: COLOR_GREEN + '44',
                color: COLOR_GREEN,
                marginRight: '5px',
                fontSize: '12px',
                borderRadius: '900px',
              }}
            >
              {discountPercentage}% off
            </span>
            Yearly Discount
          </Typography>
          <Typography variant="body2" sx={{ color: COLOR_TEXT_GREY }}>
            -{currencySymbol}
            {formattedTotalYearlyDiscount}{' '}
          </Typography>
        </Grid>

        {/* Upgrade Modal */}
        <Grid
          item
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          xs={12}
        >
          <Typography variant="body1" sx={{ fontSize: '18px' }}>
            Total Amount Due Today
          </Typography>
          {finalDueAmount ? (
            <Typography
              variant="body1"
              sx={{ fontWeight: 'bold', color: COLOR_PRIMARY_2, fontSize: '24px' }}
            >
              {exchangeRates[userLocalCurrency].symbol}
              {formatCurrencyBasedOnCountryCode(finalDueAmount, userLocalCurrency)}
            </Typography>
          ) : (
            <CircularProgress />
          )}
        </Grid>

        {paymentFailed && (
          <Grid
            item
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            xs={12}
            sx={{ marginTop: '25px' }}
          >
            <Alert
              severity="error"
              onClose={() => {
                setPaymentFailed(false);
                setPaymentFailedReason('');
              }}
              sx={{ width: '100%' }}
            >
              Payment Failed. Please try again.
              {paymentFailedReason && (
                <Typography variant="body2">Reason: {paymentFailedReason}</Typography>
              )}
            </Alert>
          </Grid>
        )}

        {!showNewCardForm ? (
          <Grid
            item
            container
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            xs={12}
            sx={{ marginTop: '25px' }}
          >
            <Button
              sx={{
                background: COLOR_PRIMARY_2,
                color: 'white',
                width: '100%',
                ':hover': {
                  background: COLOR_PRIMARY_1,
                  color: 'white',
                },
              }}
              onClick={upgradeSubscription}
              disabled={subscriptionUpgradeLoading}
            >
              Confirm Changes {subscriptionUpgradeLoading && <CircularProgress />}
            </Button>
          </Grid>
        ) : (
          <Grid
            item
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            xs={12}
            sx={{ mt: 2 }}
          >
            <Grid
              container
              item
              sx={{
                border: `2px solid ${COLOR_GREY}`,
                borderRadius: 10,
                padding: theme.spacing(2, 3, 2),
                boxShadow: theme.shadows[5],
              }}
            >
              <Typography variant="h6" sx={{ marginBottom: '10px' }}>
                Update Payment Method
              </Typography>
              <form onSubmit={handleNewCardSubmit} style={{ width: '100%' }}>
                <CardElement
                  options={{
                    style: {
                      base: {
                        fontSize: '16px',
                        color: '#424770',
                        '::placeholder': {
                          color: '#aab7c4',
                        },
                      },
                      invalid: {
                        color: '#9e2146',
                      },
                    },
                  }}
                />
                <Button
                  type="submit"
                  sx={{
                    background: COLOR_PRIMARY_2,
                    color: 'white',
                    width: '100%',
                    mt: 2,
                    ':hover': {
                      background: COLOR_PRIMARY_1,
                      color: 'white',
                    },
                  }}
                  disabled={subscriptionUpgradeLoading}
                >
                  Confirm Changes {subscriptionUpgradeLoading && <CircularProgress />}
                </Button>
              </form>
            </Grid>
          </Grid>
        )}

        {/* Reviews */}
        <SenjaWidget isMobile={true} />
      </Grid>
    </Grid>
  );
};

const UpgradeYearlyPlanModal = ({ user, setShowUpgradeYearlySubscriptionModal }) => {
  const handleClose = (args: any = null) => {
    setShowUpgradeYearlySubscriptionModal(false);
  };

  const subscriptionPlan = user.subscriptionPlan;
  const stripeAccount = useSelector((state: any) => state.dashboard.user.stripeAccount) || 'airbtics_uk';
  const stripePromiseKey = IS_TEST_MODE
    ? stripeConfig[stripeAccount]?.promiseKeyTest
    : stripeConfig[stripeAccount]?.promiseKey;
  const stripePromise = loadStripe(stripePromiseKey);

  return (
    <Modal open={true} onClose={handleClose} sx={{ zIndex: 2000 }}>
      <Elements stripe={stripePromise}>
        <StripeContext plan={subscriptionPlan} handleClose={handleClose} />
      </Elements>
    </Modal>
  );
};

export default UpgradeYearlyPlanModal;
